/**
 * Helper functions per WhatsApp chat store.
 * Funzioni pure senza accesso a Zustand state — riusabili tra le slice.
 */

import type { ChatOverview, Message } from '../../types/whatsappChat';
import { normalizeChatId } from '../../utils/whatsappHelpers';
import { debugLog } from '../../../components/whatsapp2/chat/ChatDebugPanel';

export { normalizeChatId };

/**
 * Aggiorna chats[i].lastMessage dal Message arrivato via realtime e riordina la lista.
 * Se la chat non esiste ancora in `chats` (es. nuovo contatto che ti scrive per la prima volta),
 * la crea al volo con i dati minimi del messaggio.
 * Indipendente da refreshChats() (WAHA polling) — utile quando NOWEB ha /overview stale.
 */
export function applyMessageToChats(
  chats: ChatOverview[],
  message: Message,
): ChatOverview[] {
  if (message.type === 'system') return chats;
  const normalizedMsgChatId = normalizeChatId(message.chatId);
  const newTs = Math.floor(message.timestamp.getTime() / 1000);

  const existingIndex = chats.findIndex(c => normalizeChatId(c.id) === normalizedMsgChatId);

  // Helper per costruire il lastMessage
  const buildLastMessage = () => ({
    body: message.content || '',
    timestamp: newTs,
    fromMe: message.direction === 'outbound',
    type: message.type,
    senderJid: message.senderJid,
    senderName: message.senderName,
  });

  // Helper per riordinare: pinned in cima, poi per timestamp desc
  const sorted = (arr: ChatOverview[]) => [...arr].sort((a, b) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    return (b.lastMessage?.timestamp || 0) - (a.lastMessage?.timestamp || 0);
  });

  // Caso 1: chat NON esiste ancora → crea entry minimale (nuovo contatto)
  if (existingIndex === -1) {
    const fallbackName = message.senderName
      || message.chatId.split('@')[0]
      || 'Sconosciuto';
    const newChat: ChatOverview = {
      id: message.chatId,
      name: fallbackName,
      isGroup: message.chatId.endsWith('@g.us'),
      pinned: false,
      archived: false,
      unreadCount: message.direction === 'inbound' ? 1 : 0,
      lastMessage: buildLastMessage(),
    };
    debugLog('Webhook', `nuova chat creata da realtime: "${fallbackName}" (${message.chatId.slice(0, 25)})`);
    return sorted([...chats, newChat]);
  }

  // Caso 2: chat esiste → aggiorna lastMessage solo se più recente
  const existing = chats[existingIndex];
  if (existing.lastMessage && existing.lastMessage.timestamp >= newTs) {
    return chats; // niente da fare
  }

  const updated = chats.map((c, i) =>
    i === existingIndex ? { ...c, lastMessage: buildLastMessage() } : c
  );
  return sorted(updated);
}

/**
 * Verifica se un messaggio è già presente nella lista esistente.
 * Usa ID esatto come primario, e fallback su timestamp+direction+content.
 * Tolleranza timestamp adattiva:
 * - 30s per messaggi con testo (content identico = quasi certamente stesso messaggio)
 * - 15s per media senza caption (WAHA può generare gap fino a 13s)
 */
export function isDuplicateMessage(existing: Message[], newMsg: Message, debug = false): boolean {
  const newTimestampSec = Math.floor(newMsg.timestamp.getTime() / 1000);

  const matchedById = existing.find((m) => m.id === newMsg.id);
  const matchedByContent = existing.find((m) => {
    const existingTimestampSec = Math.floor(m.timestamp.getTime() / 1000);
    const timeDiff = Math.abs(existingTimestampSec - newTimestampSec);
    const dirMatch = m.direction === newMsg.direction;
    if (!dirMatch) return false;
    // Per media senza testo: tolleranza 15s + match su type
    if (!newMsg.content && !m.content) {
      return timeDiff <= 15 && m.type === newMsg.type;
    }
    // Per messaggi con testo: tolleranza 30s + match su content
    return timeDiff <= 30 && m.content === newMsg.content;
  });

  const isDuplicate = !!matchedById || !!matchedByContent;

  if (debug && isDuplicate) {
    console.log('[Dedup] DUPLICATE FOUND:', {
      newId: newMsg.id,
      matchedById: matchedById?.id || null,
      matchedByContent: matchedByContent?.id || null,
      newContent: newMsg.content?.substring(0, 30),
      newTimestamp: newMsg.timestamp,
    });
  }

  return isDuplicate;
}

/** Rileva format UUID (da process-whatsapp-queue) */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Deduplica messaggi DB: la coda inserisce righe con UUID, il webhook inserisce
 * righe con WAHA ID per lo stesso messaggio. Rimuove le righe UUID quando
 * esiste la corrispondente riga WAHA.
 */
export function deduplicateDbMessages(messages: Message[]): Message[] {
  const uuidMsgs = messages.filter(m => UUID_REGEX.test(m.id));
  if (uuidMsgs.length === 0) return messages;

  const nonUuidMsgs = messages.filter(m => !UUID_REGEX.test(m.id));
  const uuidIdsToRemove = new Set<string>();

  for (const uuid of uuidMsgs) {
    const match = nonUuidMsgs.find(w =>
      w.direction === uuid.direction &&
      Math.abs(w.timestamp.getTime() - uuid.timestamp.getTime()) < 10000 &&
      (w.content === uuid.content || !uuid.content)
    );
    if (match) {
      uuidIdsToRemove.add(uuid.id);
    }
  }

  return uuidIdsToRemove.size > 0
    ? messages.filter(m => !uuidIdsToRemove.has(m.id))
    : messages;
}
